import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardActions,
  CardMedia,
  Divider,
  IconButton,
  Typography
} from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import { useTheme } from "@mui/system";

const AutobiaCard = ({
  children,
  title,
  subtitle,
  avatar,
  action,
  image,
  imageHeight = 200,
  actions,
  elevation = 1,
  variant = "elevation",
  sx = {},
  headerSx = {},
  contentSx = {},
  actionsSx = {},
  disablePadding = false,
  hoverable = false,
  clickable = false,
  onClick,
}) => {
  const theme = useTheme();

  const cardStyles = {
    borderRadius: "12px",
    backgroundColor: "white",
    border: variant === "outlined" ? `1px solid ${theme.palette.gray.g200}` : "none",
    transition: "all 0.2s ease-in-out",
    ...(hoverable && {
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: theme.shadows[4],
      },
    }),
    ...(clickable && {
      cursor: "pointer",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: theme.shadows[3],
      },
    }),
    ...sx,
  };

  return (
    <Card
      elevation={variant === "outlined" ? 0 : elevation}
      sx={cardStyles}
      onClick={clickable ? onClick : undefined}
    >
      {/* Card Header */}
      {(title || subtitle || avatar || action) && (
        <>
          <CardHeader
            avatar={avatar}
            action={action || (
              <IconButton size="small" sx={{ color: "gray.g400" }}>
                <MoreVert />
              </IconButton>
            )}
            title={
              title && (
                <Typography variant="h6" fontWeight={600} color="gray.g700">
                  {title}
                </Typography>
              )
            }
            subheader={
              subtitle && (
                <Typography variant="body2" color="gray.g500">
                  {subtitle}
                </Typography>
              )
            }
            sx={{
              pb: image ? 0 : 1,
              "& .MuiCardHeader-content": {
                overflow: "hidden",
              },
              ...headerSx,
            }}
          />
          {!image && <Divider sx={{ backgroundColor: "gray.g100" }} />}
        </>
      )}

      {/* Card Media */}
      {image && (
        <CardMedia
          component="img"
          height={imageHeight}
          image={image}
          sx={{
            objectFit: "cover",
          }}
        />
      )}

      {/* Card Content */}
      {children && (
        <CardContent
          sx={{
            ...(disablePadding && { p: 0, "&:last-child": { pb: 0 } }),
            ...(!disablePadding && { p: 3, "&:last-child": { pb: 3 } }),
            ...contentSx,
          }}
        >
          {children}
        </CardContent>
      )}

      {/* Card Actions */}
      {actions && (
        <>
          <Divider sx={{ backgroundColor: "gray.g100" }} />
          <CardActions
            sx={{
              p: 2,
              justifyContent: "flex-end",
              ...actionsSx,
            }}
          >
            {actions}
          </CardActions>
        </>
      )}
    </Card>
  );
};

// Specialized Card Variants
export const AutobiaStatsCard = ({
  title,
  value,
  icon,
  trend,
  trendLabel,
  color = "primary",
  sx = {},
}) => {
  const theme = useTheme();
  
  const colorMap = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
  };

  return (
    <AutobiaCard
      sx={{
        p: 3,
        background: `linear-gradient(135deg, ${colorMap[color]}08 0%, ${colorMap[color]}02 100%)`,
        border: `1px solid ${colorMap[color]}20`,
        ...sx,
      }}
      disablePadding
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ flex: 1 }}>
          <Typography variant="body2" color="gray.g600" sx={{ mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={700} color="gray.g800" sx={{ mb: 1 }}>
            {value}
          </Typography>
          {trend && (
            <Typography
              variant="caption"
              sx={{
                color: trend > 0 ? "success.main" : "error.main",
                fontWeight: 600,
              }}
            >
              {trend > 0 ? "+" : ""}{trend}% {trendLabel}
            </Typography>
          )}
        </div>
        {icon && (
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              backgroundColor: colorMap[color],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            {icon}
          </div>
        )}
      </div>
    </AutobiaCard>
  );
};

export const AutobiaImageCard = ({
  image,
  title,
  description,
  actions,
  imageHeight = 200,
  sx = {},
}) => {
  return (
    <AutobiaCard
      image={image}
      imageHeight={imageHeight}
      actions={actions}
      hoverable
      sx={sx}
    >
      {title && (
        <Typography variant="h6" fontWeight={600} color="gray.g700" sx={{ mb: 1 }}>
          {title}
        </Typography>
      )}
      {description && (
        <Typography variant="body2" color="gray.g600">
          {description}
        </Typography>
      )}
    </AutobiaCard>
  );
};

export default AutobiaCard;